import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  inject,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogClose,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatError, MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { TranslatePipe } from '@ngx-translate/core';
import { Observable, combineLatest } from 'rxjs';
import { ContextService } from '@core/providers/context/context.service';
import { KeyboardStatusService } from '@core/providers/keyboard-status/keyboard-status.service';
import { KeyboardUtils } from '@core/utils/events/keyboard.utils';
import {
  DialogActionsDirective,
  DialogComponent,
  DialogContentDirective,
  DialogTitleDirective,
} from '@shared/components/dialog/dialog.component';
import { GroupsService } from '@shared/components/groups/groups.service';
import { ValidationErrorsComponent } from '@shared/components/validation-errors/validation-errors.component';
import { CONSTANTS } from '@shared/constants';
import { ErrorsDirective } from '@shared/directives/error/errors.directive';
import { FormModels } from '@shared/form-models';
import { IGroupPayload } from '@shared/interfaces/groups/add-group-payload.interface';
import { IGroup } from '@shared/interfaces/groups/group.interface';

export interface NewGroupDialogData {
  /** Present when editing/renaming an existing group. */
  group?: IGroup;
  /** Category to preselect when creating a group from a category band. */
  presetCategoryUuid?: string | null;
}

export const newGroupDialogConfig: MatDialogConfig = {
  width: '480px',
  disableClose: true,
  panelClass: 'dialog-fit-content',
};

@Component({
  selector: 'app-new-group-dialog',
  templateUrl: './new-group-dialog.component.html',
  styleUrls: ['./new-group-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(window:keyup)': 'keyEvent($event)' },
  imports: [
    ReactiveFormsModule,
    DialogComponent,
    DialogTitleDirective,
    DialogContentDirective,
    DialogActionsDirective,
    MatButton,
    MatDialogClose,
    MatFormField,
    MatLabel,
    MatHint,
    MatInput,
    MatError,
    MatSelect,
    MatOption,
    ErrorsDirective,
    ValidationErrorsComponent,
    TranslatePipe,
  ],
})
export class NewGroupDialogComponent implements OnInit {
  data = inject<NewGroupDialogData | null>(MAT_DIALOG_DATA);

  readonly NO_CATEGORY_UUID = CONSTANTS.GROUPS.NO_CATEGORY_UUID;

  categories: IGroup[] = [];
  errors = FormModels.AccountAccount.GroupErrors;
  accountChildrenNameSingular = '';
  accountChildrenNamePlural = '';

  readonly nameInputRef = viewChild<ElementRef>('nameInput');

  private mdDialogRef = inject<MatDialogRef<NewGroupDialogComponent, IGroup>>(MatDialogRef);
  private groupsService = inject(GroupsService);
  private formBuilder = inject(FormBuilder);
  private keyboardStatusService = inject(KeyboardStatusService);
  private contextService = inject(ContextService);
  private destroyRef = inject(DestroyRef);

  private groups: IGroup[] = [];
  private escModalStatus = false;

  readonly groupForm: FormGroup = this.createForm();

  get selectedGroup(): IGroup | null {
    return this.data?.group ?? null;
  }

  keyEvent(event: KeyboardEvent) {
    if (KeyboardUtils.pressedEscape(event) && this.escModalStatus) {
      this.mdDialogRef.close();
    }
  }

  ngOnInit() {
    this.accountChildrenNameSingular = this.contextService.getAccountChildrenNameTranslation(true);
    this.accountChildrenNamePlural = this.contextService.getAccountChildrenNameTranslation(false);
    combineLatest([this.getCategories$(), this.groupsService.getGroups$()])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([categories, groups]: [IGroup[], IGroup[]]) => {
        this.categories = categories;
        this.groups = groups;
        this.groupForm.controls.name.updateValueAndValidity();
      });
    this.keyboardStatusService
      .getEscModalStatus()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(status => (this.escModalStatus = status));
    this.focusNameInput();
  }

  save() {
    const selectedGroup = this.selectedGroup;

    if (this.groupForm.valid) {
      if (selectedGroup) {
        this.updateExisting(selectedGroup);
      } else {
        this.saveNew();
      }
    } else {
      this.groupForm.controls.name.markAsTouched();
      this.groupForm.controls.name.markAsDirty();
    }
  }

  private createForm(): FormGroup {
    const minLength = 2;
    const presetCategoryUuid = this.selectedGroup
      ? (this.selectedGroup.category ?? this.NO_CATEGORY_UUID)
      : (this.data?.presetCategoryUuid ?? this.NO_CATEGORY_UUID);

    return this.formBuilder.group({
      name: new FormControl(this.selectedGroup?.name ?? '', [
        Validators.required,
        Validators.minLength(minLength),
        this.isUniqueGroupName.bind(this),
      ]),
      categoryUuid: new FormControl(presetCategoryUuid),
    });
  }

  private isUniqueGroupName(control: AbstractControl) {
    const allGroups = [...this.categories, ...this.groups];
    const selectedGroup = this.selectedGroup;
    const otherGroups = selectedGroup
      ? allGroups.filter(group => group.uuid !== selectedGroup.uuid)
      : allGroups;
    const isExisting = !!otherGroups.find(group => group.name === control.value);

    return isExisting ? { isUnique: {} } : null;
  }

  private updateExisting(selectedGroup: IGroup) {
    const payload: IGroupPayload = {
      ...this.getPayloadBase(),
      venues: (selectedGroup.venues ?? []).map(venue => venue.uuid),
    };

    this.groupsService
      .updateGroup$(selectedGroup.uuid, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(updatedGroup => this.handleResponseData(updatedGroup));
  }

  private saveNew() {
    const payload: IGroupPayload = {
      ...this.getPayloadBase(),
      venues: [],
    };

    this.groupsService
      .addGroup$(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(newGroup => this.handleResponseData(newGroup));
  }

  private getPayloadBase(): Omit<IGroupPayload, 'venues'> {
    const categoryUuid: string = this.groupForm.controls.categoryUuid.value;

    return {
      name: this.groupForm.controls.name.value,
      isCategory: false,
      venueGroups: [],
      category: categoryUuid === this.NO_CATEGORY_UUID ? null : categoryUuid,
    };
  }

  private handleResponseData(group: IGroup | null) {
    if (group?.uuid) {
      this.mdDialogRef.close(group);
    }
  }

  private getCategories$(): Observable<IGroup[]> {
    return this.groupsService.getCategories$();
  }

  private focusNameInput() {
    setTimeout(() => {
      this.nameInputRef()?.nativeElement.focus();
    }, CONSTANTS.TIME_IN_MS.TIME_250);
  }
}
